import { RECENT_PROBE_TRUST_MS } from './refusal-recheck-gate';

/**
 * When the app comes back to the foreground, may it reuse what it already believes about the
 * connection, or must it go and check the server again?
 *
 * The check itself is one small HTTP request. Firing it on every single return to the app was
 * wasteful — people flick out of an app and back constantly — so S7 (#4027 Step 25) added a rule:
 * skip the check if one finished less than RECENT_PROBE_TRUST_MS ago. That rule looked only at HOW
 * OLD the last answer was. It never looked at WHAT the answer said, and that is what broke.
 *
 * Why the two are not the same thing (#4318). On Android the app runs inside a WebView, and while
 * the app sits in the background the WebView is not allowed to use the network at all. Every
 * request made from that sleeping page fails within a few milliseconds. Our connection check is one
 * of those requests, so it fails too, and the app concludes it is offline. That conclusion says
 * nothing about the phone's network — it only says the app was asleep.
 *
 * A real customer hit exactly this. Their last check finished while the app was asleep and said
 * "offline". They came back to the app 1.9 seconds later, which is well inside the 30-second
 * window, so no new check ran. Nothing re-checked on a timer either, so the app went on showing
 * "Offline" for the rest of the session while requests to our own server were succeeding the whole
 * time. (The timer half of that has since been closed too — the six-minute foreground tick now
 * re-checks while the app believes it is offline, df-eabc7656368c. The rule below is still what
 * makes the fix immediate on return rather than up to six minutes later.)
 *
 * The rule below fixes that by asking a different question. An "offline" belief is the only one
 * worth re-checking, because it is the one that might have been produced by the app being asleep
 * rather than by the network being down — and it is also the one that hurts the user when it is
 * wrong. An "online" belief is not worth re-checking: if it has gone stale, the next request that
 * fails will say so.
 *
 * Note this deliberately does NOT try to work out whether the page was hidden when the last check
 * ran. That was the first design and it is harder to get right: a check that starts while the app
 * is awake, is suspended part-way through when the app goes to sleep, and only reports its failure
 * once the app wakes up again looks "awake" at both ends, so the app-was-asleep case slips through.
 * Looking at the belief we would actually reuse has no such gap.
 *
 * The refusal gate in refusal-recheck-gate.ts deliberately keeps the plain age rule. It runs when a
 * feature is about to tell someone "you cannot do this, you are offline", and re-checking there on
 * every offline belief would make a genuinely offline person wait out the full request timeout
 * before each refusal — the exact problem that gate was written to avoid.
 */
export function shouldSkipResumeProbe(input: {
  /** What the app believes right now — `connectionState === ConnectionState.Online`. */
  believedOnline: boolean;
  /** How long ago the last check settled, either way. `Infinity` when none ever has. */
  msSinceLastProbe: number;
}): boolean {
  // #4318: an offline belief is never fresh enough to reuse. Go and look.
  if (!input.believedOnline) return false;
  // S7 (#4027 Step 25): an online belief from the last RECENT_PROBE_TRUST_MS still stands.
  return input.msSinceLastProbe < RECENT_PROBE_TRUST_MS;
}
